import { DateTime } from 'luxon';
import {
  SkinConcernSeverityEnum,
  SkinImageDirectionEnum,
  type ISkinScan,
  type ISkinScanImage,
  type ISkinScanPublicSummary,
} from '@torim-app/types';

/**
 * The images of a scan, with back-compat for legacy single-image scans: when a scan
 * has no `images` array, fall back to its primary `imageUrl` as a single front image.
 */
export function scanImages(
  scan: Pick<ISkinScan | ISkinScanPublicSummary, 'images' | 'imageUrl'> & { imageFileId?: string },
): ISkinScanImage[] {
  if (scan.images?.length) return scan.images;
  if (scan.imageUrl) {
    return [{ url: scan.imageUrl, fileId: scan.imageFileId, direction: SkinImageDirectionEnum.Front }];
  }
  return [];
}

/** Color for a 0–100 score (higher = healthier => greener). */
export function scoreColor(score: number): string {
  if (score >= 70) return '#22c55e'; // green-500
  if (score >= 40) return '#f59e0b'; // amber-500
  return '#ef4444'; // red-500
}

export function severityColor(severity: SkinConcernSeverityEnum): string {
  switch (severity) {
    case SkinConcernSeverityEnum.Low:
      return '#22c55e';
    case SkinConcernSeverityEnum.Moderate:
      return '#f59e0b';
    case SkinConcernSeverityEnum.High:
      return '#ef4444';
    default:
      return '#9ca3af';
  }
}

export function severityLabel(severity: SkinConcernSeverityEnum): string {
  switch (severity) {
    case SkinConcernSeverityEnum.Low:
      return 'תקין';
    case SkinConcernSeverityEnum.Moderate:
      return 'בינוני';
    case SkinConcernSeverityEnum.High:
      return 'דורש תשומת לב';
    default:
      return '';
  }
}

/** Color for a signed delta (improvement = green). */
export function deltaColor(delta: number): string {
  if (delta > 0) return '#22c55e';
  if (delta < 0) return '#ef4444';
  return '#9ca3af';
}

export function formatScanDate(date: Date | string | undefined): string {
  if (!date) return '';
  return DateTime.fromISO(String(date)).setLocale('he').toLocaleString(DateTime.DATE_MED);
}

export function formatScanDateTime(date: Date | string | undefined): string {
  if (!date) return '';
  return DateTime.fromISO(String(date)).setLocale('he').toLocaleString(DateTime.DATETIME_MED);
}
